import React, { useEffect, useMemo, useState } from 'react'
import { getOperationsReport, listJobAssignments, listJobs, listWorkerProfiles } from '../../../services/access'
import type {
  AccessibleLocation,
  Job,
  JobAssignment,
  OperationsReport,
  OperationsReportDetailRow,
  OperationsReportFilters,
  Organization,
  WorkerProfileDetail,
} from '../../../types/access'
import { useI18n } from '../../../i18n/I18nContext'
import { EmptyState, ErrorBanner, LoadingState } from './AdminUiPrimitives'
import { useAccessContext } from '../../../context/AccessContext'
import { useIndustryTerminology, type IndustryLabelKey } from '../../../hooks/useIndustryTerminology'

interface ReportsPanelProps {
  organizations: Organization[]
  accessibleLocations: AccessibleLocation[]
  selectedOrgId: string
  canViewReports: boolean
  onOpenPayrollExport?: () => void
}

const pad = (value: number) => String(value).padStart(2, '0')
const dateInputValue = (date = new Date()) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
const defaultStartDate = () => {
  const date = new Date()
  date.setDate(date.getDate() - 6)
  return dateInputValue(date)
}
const readable = (value?: string | null) => (value || '').replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase()) || '-'
const dateTime = (value?: string | null) => value ? new Date(value).toLocaleString() : '-'
const numberLabel = (value: unknown, digits = 0) => {
  const numeric = Number(value || 0)
  return Number.isFinite(numeric) ? numeric.toLocaleString(undefined, { maximumFractionDigits: digits, minimumFractionDigits: digits }) : '0'
}

const locationLabel = (location: AccessibleLocation) => location.name || location.label || location.location_key || 'Unnamed location'
const findLocationLabel = (locations: AccessibleLocation[], locationId?: string) => locations.find((location) => location.id === locationId)?.name || locations.find((location) => location.id === locationId)?.label || ''
const findOrgLabel = (organizations: Organization[], orgId?: string) => organizations.find((org) => org.id === orgId)?.name || ''
const workerName = (worker: WorkerProfileDetail) => (
  worker.workerName ||
  worker.display_name ||
  [worker.first_name, worker.last_name].filter(Boolean).join(' ').trim() ||
  worker.workerEmail ||
  worker.worker_email ||
  'Worker'
)
const workerLabel = (worker: WorkerProfileDetail) => {
  const email = worker.workerEmail || worker.worker_email
  return email ? `${workerName(worker)} - ${email}` : workerName(worker)
}
const assignmentLabel = (assignment: JobAssignment) => [
  assignment.job_title || 'Assignment',
  assignment.worker_display_name || assignment.worker_email,
  readable(assignment.status),
].filter(Boolean).join(' - ')

const KpiCard: React.FC<{ label: string; value: string; helper?: string }> = ({ label, value, helper }) => (
  <article className="admin-checklist-item" style={{ alignItems: 'flex-start' }}>
    <span className="helper">{label}</span>
    <strong style={{ fontSize: '1.45rem', lineHeight: 1.1 }}>{value}</strong>
    {helper ? <span className="helper">{helper}</span> : null}
  </article>
)

const ReportCatalog: React.FC<{
  canOpenPayroll: boolean
  onOpenPayrollExport?: () => void
}> = ({ canOpenPayroll, onOpenPayrollExport }) => (
  <section className="admin-report-catalog" aria-label="Report catalog">
    <article className="admin-report-group">
      <div>
        <h4>Workforce</h4>
        <p className="helper">Current attendance and active worker state in the visible tenant, organization, and location scope.</p>
      </div>
      <span className="status-pill">Operations summary</span>
    </article>
    <article className="admin-report-group">
      <div>
        <h4>Jobs & Coverage</h4>
        <p className="helper">Scheduled demand, accepted-active fill, open coverage, and completed work by location and job.</p>
      </div>
      <span className="status-pill">Tables</span>
    </article>
    <article className="admin-report-group">
      <div>
        <h4>Attendance & Time</h4>
        <p className="helper">Late arrivals, missing shifts, open worker shifts, and approval rows for the selected date window.</p>
      </div>
      <span className="status-pill">Exceptions</span>
    </article>
    <article className="admin-report-group">
      <div>
        <h4>Financial / Payroll Export</h4>
        <p className="helper">Payroll Export owns downloadable files, export history, precision, currency, and marked-exported state.</p>
      </div>
      <button type="button" className="btn btn-secondary" onClick={onOpenPayrollExport} disabled={!canOpenPayroll}>Open Payroll Export</button>
    </article>
  </section>
)

const DetailTable: React.FC<{ title: string; rows: OperationsReportDetailRow[]; empty: string; labels: (key: IndustryLabelKey) => string; explicitIndustry: boolean }> = ({ title, rows, empty, labels, explicitIndustry }) => (
  <section className="stack" style={{ gap: '.75rem' }}>
    <h4 style={{ margin: 0 }}>{title}</h4>
    {rows.length ? (
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>{explicitIndustry ? labels('worker') : 'Worker'}</th>
              <th>{explicitIndustry ? labels('location') : 'Location'}</th>
              <th>{explicitIndustry ? labels('job') : 'Job / order'}</th>
              <th>Scheduled</th>
              <th>Actual</th>
              <th>Status</th>
              <th>Reason</th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 25).map((row, index) => (
              <tr key={`${row.workerEmail || row.workerName || 'row'}-${index}`}>
                <td>
                  <strong>{row.workerName || (explicitIndustry ? labels('worker') : 'Worker')}</strong>
                  <div className="helper">{row.workerEmail || '-'}</div>
                </td>
                <td>{row.locationName || '-'}</td>
                <td>{row.jobTitle || '-'}</td>
                <td>{dateTime(row.scheduledStart)}<br />{dateTime(row.scheduledEnd)}</td>
                <td>{dateTime(row.actualStart)}<br />{dateTime(row.actualEnd)}</td>
                <td>{readable(row.approvalState || row.status)}</td>
                <td>{row.reason || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ) : (
      <EmptyState title={empty} description="No matching rows were returned for the selected scope." />
    )}
  </section>
)

export const ReportsPanel: React.FC<ReportsPanelProps> = ({
  organizations,
  accessibleLocations,
  selectedOrgId,
  canViewReports,
  onOpenPayrollExport,
}) => {
  const { t } = useI18n()
  const { currentBrandName, currentLogoUrl, brandStyleVars } = useAccessContext()
  const industryLabels = useIndustryTerminology()
  const [filters, setFilters] = useState<OperationsReportFilters>({
    startDate: defaultStartDate(),
    endDate: dateInputValue(),
    companyOrgId: organizations.find((org) => org.id === selectedOrgId && org.org_type === 'company')?.id || '',
    agencyOrgId: organizations.find((org) => org.id === selectedOrgId && org.org_type === 'agency')?.id || '',
  })
  const [jobs, setJobs] = useState<Job[]>([])
  const [workers, setWorkers] = useState<WorkerProfileDetail[]>([])
  const [assignments, setAssignments] = useState<JobAssignment[]>([])
  const [report, setReport] = useState<OperationsReport | null>(null)
  const [loading, setLoading] = useState(false)
  const [optionsLoading, setOptionsLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!canViewReports) return
    let active = true
    setOptionsLoading(true)
    Promise.all([
      listJobs({ pageSize: 250 }),
      listWorkerProfiles({ pageSize: 250 }),
      listJobAssignments({ pageSize: 250 }),
    ]).then(([jobRows, workerRows, assignmentRows]) => {
      if (!active) return
      setJobs(jobRows)
      setWorkers(workerRows)
      setAssignments(assignmentRows)
    }).catch(() => {
      if (active) setError('Report filter options could not be loaded.')
    }).finally(() => {
      if (active) setOptionsLoading(false)
    })
    return () => { active = false }
  }, [canViewReports])

  const cleanFilters = useMemo(
    () => Object.fromEntries(Object.entries(filters).filter(([, value]) => Boolean(value))) as OperationsReportFilters,
    [filters],
  )

  const activeFilterLabels = useMemo(() => {
    const labels: string[] = []
    if (filters.startDate || filters.endDate) labels.push(`${filters.startDate || 'start'} to ${filters.endDate || 'today'}`)
    if (filters.locationId) labels.push(`Location: ${findLocationLabel(accessibleLocations, filters.locationId) || 'selected location'}`)
    if (filters.companyOrgId) labels.push(`Company: ${findOrgLabel(organizations, filters.companyOrgId) || 'selected company'}`)
    if (filters.agencyOrgId) labels.push(`Agency: ${findOrgLabel(organizations, filters.agencyOrgId) || 'selected agency'}`)
    if (filters.jobId) labels.push(`Job: ${jobs.find((job) => job.id === filters.jobId)?.title || 'selected job'}`)
    if (filters.jobAssignmentId) labels.push(`Assignment: ${assignments.find((assignment) => assignment.id === filters.jobAssignmentId)?.job_title || 'selected assignment'}`)
    if (filters.workerId) labels.push(`Worker: ${workers.find((worker) => worker.worker_user_id === filters.workerId)?.workerName || 'selected worker'}`)
    if (filters.approvalState) labels.push(`Approval: ${readable(filters.approvalState)}`)
    if (filters.search) labels.push(`Search: ${filters.search}`)
    return labels
  }, [accessibleLocations, assignments, filters, jobs, organizations, workers])

  const clearFilters = () => {
    setFilters({
      startDate: defaultStartDate(),
      endDate: dateInputValue(),
      companyOrgId: organizations.find((org) => org.id === selectedOrgId && org.org_type === 'company')?.id || '',
      agencyOrgId: organizations.find((org) => org.id === selectedOrgId && org.org_type === 'agency')?.id || '',
    })
  }

  const loadReport = () => {
    if (!canViewReports) return
    setLoading(true)
    setError('')
    getOperationsReport(cleanFilters)
      .then(setReport)
      .catch((err: any) => setError(err?.response?.data?.message || 'Unable to load operations report.'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadReport()
  }, [JSON.stringify(cleanFilters), canViewReports])

  if (!canViewReports) {
    return <EmptyState title={t('Reports unavailable')} description={t('Your current role does not include timesheet or export reporting access.')} />
  }

  const summary = report?.summary
  return (
    <section className="panel workspace-panel stack" data-testid="admin-reports-panel" style={brandStyleVars}>
      <div className="admin-panel-toolbar" style={{ borderTop: '3px solid var(--brand-primary, var(--primary))', paddingTop: '.75rem' }}>
        <div className="inline-group" style={{ alignItems: 'center' }}>
          {currentLogoUrl ? <img src={currentLogoUrl} alt="" className="admin-brand-logo admin-brand-logo--sm" /> : null}
          <div className="stack" style={{ gap: '.25rem' }}>
            <p className="helper" style={{ margin: 0 }}>{currentBrandName}</p>
          <h3 style={{ margin: 0 }}>{t('Operations reports')}</h3>
          <p className="text-muted">
            {industryLabels.explicitIndustry ? `Live ${industryLabels.label('workers').toLowerCase()}, scheduling, attendance, and approval metrics for your visible tenant, organization, and ${industryLabels.label('location').toLowerCase()} scope.` : t('Live workforce, scheduling, attendance, and approval metrics for your visible tenant, organization, and location scope.')}
          </p>
          </div>
        </div>
        <button type="button" className="btn btn-secondary" onClick={loadReport} disabled={loading}>
          {loading ? t('Refreshing...') : t('Refresh')}
        </button>
      </div>

      {error ? <ErrorBanner message={error} /> : null}

      <ReportCatalog canOpenPayroll={Boolean(onOpenPayrollExport)} onOpenPayrollExport={onOpenPayrollExport} />

      <div className="admin-filter-grid">
        <label>
          <span>{t('Start date')}</span>
          <input className="input" type="date" value={filters.startDate || ''} onChange={(event) => setFilters((current) => ({ ...current, startDate: event.target.value }))} />
        </label>
        <label>
          <span>{t('End date')}</span>
          <input className="input" type="date" value={filters.endDate || ''} onChange={(event) => setFilters((current) => ({ ...current, endDate: event.target.value }))} />
        </label>
        <label>
          <span>{industryLabels.explicitIndustry ? industryLabels.label('location') : t('Location')}</span>
          <select className="input" value={filters.locationId || ''} onChange={(event) => setFilters((current) => ({ ...current, locationId: event.target.value }))}>
            <option value="">{industryLabels.explicitIndustry ? `All visible ${industryLabels.label('locations').toLowerCase()}` : t('All visible locations')}</option>
            {accessibleLocations.map((location) => <option key={location.id} value={location.id}>{locationLabel(location)}</option>)}
          </select>
        </label>
        <label>
          <span>{t('Company')}</span>
          <select className="input" value={filters.companyOrgId || ''} onChange={(event) => setFilters((current) => ({ ...current, companyOrgId: event.target.value }))}>
            <option value="">{t('All visible companies')}</option>
            {organizations.filter((org) => org.org_type === 'company').map((org) => <option key={org.id} value={org.id}>{org.name}</option>)}
          </select>
        </label>
        <label>
          <span>{t('Agency')}</span>
          <select className="input" value={filters.agencyOrgId || ''} onChange={(event) => setFilters((current) => ({ ...current, agencyOrgId: event.target.value }))}>
            <option value="">{t('All visible agencies')}</option>
            {organizations.filter((org) => org.org_type === 'agency').map((org) => <option key={org.id} value={org.id}>{org.name}</option>)}
          </select>
        </label>
        <label>
          <span>{industryLabels.explicitIndustry ? industryLabels.label('job') : t('Job / order')}</span>
          <select className="input" value={filters.jobId || ''} onChange={(event) => setFilters((current) => ({ ...current, jobId: event.target.value }))} disabled={optionsLoading}>
            <option value="">{industryLabels.explicitIndustry ? `All ${industryLabels.label('jobs').toLowerCase()}` : t('All jobs')}</option>
            {jobs.map((job) => <option key={job.id} value={job.id}>{job.title}</option>)}
          </select>
        </label>
        <label>
          <span>{industryLabels.explicitIndustry ? industryLabels.label('assignment') : t('Assignment')}</span>
          <select className="input" value={filters.jobAssignmentId || ''} onChange={(event) => setFilters((current) => ({ ...current, jobAssignmentId: event.target.value }))} disabled={optionsLoading}>
            <option value="">{industryLabels.explicitIndustry ? `All ${industryLabels.label('assignments').toLowerCase()}` : t('All assignments')}</option>
            {assignments.map((assignment) => <option key={assignment.id} value={assignment.id}>{assignmentLabel(assignment)}</option>)}
          </select>
        </label>
        <label>
          <span>{t('Approval state')}</span>
          <select className="input" value={filters.approvalState || ''} onChange={(event) => setFilters((current) => ({ ...current, approvalState: event.target.value as OperationsReportFilters['approvalState'] }))}>
            <option value="">{t('All approval states')}</option>
            {['completed', 'submitted', 'approved_supervisor', 'approved_company', 'rejected', 'disputed', 'payroll_locked', 'exported'].map((state) => (
              <option key={state} value={state}>{t(readable(state))}</option>
            ))}
          </select>
        </label>
        <label>
          <span>{industryLabels.explicitIndustry ? industryLabels.label('worker') : t('Worker')}</span>
          <select className="input" value={filters.workerId || ''} onChange={(event) => setFilters((current) => ({ ...current, workerId: event.target.value }))} disabled={optionsLoading}>
            <option value="">{industryLabels.explicitIndustry ? `All ${industryLabels.label('workers').toLowerCase()}` : t('All workers')}</option>
            {workers
              .filter((worker) => worker.worker_user_id)
              .map((worker) => <option key={worker.worker_user_id || worker.worker_profile_id} value={worker.worker_user_id}>{workerLabel(worker)}</option>)}
          </select>
        </label>
        <label>
          <span>{t('Search')}</span>
          <input className="input" value={filters.search || ''} onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))} placeholder={industryLabels.explicitIndustry ? `${industryLabels.label('worker')}, ${industryLabels.label('job').toLowerCase()}, or ${industryLabels.label('location').toLowerCase()}` : t('Worker, job, or location')} />
        </label>
      </div>

      <div className="admin-panel-toolbar" aria-live="polite">
        <div className="stack" style={{ gap: '.35rem' }}>
          <strong>{t('Applied scope')}</strong>
          <p className="helper" style={{ margin: 0 }}>
            {activeFilterLabels.length ? activeFilterLabels.join(' / ') : t('Visible tenant, organization, and location scope with no additional filters.')}
          </p>
          <p className="helper" style={{ margin: 0 }}>{t('Dates use the API date window for shift start time; generated timestamps use the browser locale display.')}</p>
        </div>
        <button type="button" className="btn btn-secondary" onClick={clearFilters} disabled={loading}>{t('Clear filters')}</button>
      </div>

      {loading && !report ? <LoadingState label={t('Loading operations report...')} /> : null}

      {summary ? (
        <>
          <div className="admin-checklist-grid">
            <KpiCard label={industryLabels.explicitIndustry ? `Active ${industryLabels.label('workers').toLowerCase()} on site` : t('Active workers on site')} value={numberLabel(summary.activeOnSite)} />
            <KpiCard label={industryLabels.explicitIndustry ? `${industryLabels.label('workers')} on break` : t('Workers on break')} value={numberLabel(summary.onBreak)} />
            <KpiCard label={t('Late arrivals')} value={numberLabel(summary.lateArrivals)} helper={`${numberLabel(summary.lateRate, 1)}% ${t('late rate')}`} />
            <KpiCard label={t('Fill rate')} value={`${numberLabel(summary.fillRate, 1)}%`} />
            <KpiCard label={t('Missing shifts / no-shows')} value={numberLabel(summary.missingShifts)} />
            <KpiCard label={t('Open shifts')} value={numberLabel(summary.openShifts)} />
            <KpiCard label={t('Completed shifts')} value={numberLabel(summary.completedShifts)} />
            <KpiCard label={t('Pending approvals')} value={numberLabel(summary.pendingApprovals)} />
            <KpiCard label={t('Approved / export-ready')} value={numberLabel(summary.approvedExportReady)} />
            <KpiCard label={t('Gross hours')} value={numberLabel(summary.grossHours, 2)} />
            <KpiCard label={t('Break hours')} value={numberLabel(summary.breakHours, 2)} />
            <KpiCard label={t('Net payable hours')} value={numberLabel(summary.netPayableHours, 2)} />
          </div>

          <div className="admin-panel-toolbar">
            <span className="helper">{t('Generated')} {dateTime(report?.generatedAt)}</span>
            <button type="button" className="btn btn-primary" onClick={onOpenPayrollExport}>
              {industryLabels.explicitIndustry ? `Open ${industryLabels.label('payroll')} Export` : t('Open Payroll Export')}
            </button>
            <span className="helper">{t('Payroll Export reuses the same visible scope and applies its backend export-ready filters before download.')}</span>
          </div>

          <div className="admin-two-column">
            <section className="stack" style={{ gap: '.75rem' }}>
              <h4 style={{ margin: 0 }}>{industryLabels.explicitIndustry ? `${industryLabels.label('location')} breakdown` : t('Location breakdown')}</h4>
              {report.byLocation.length ? (
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead><tr><th>{industryLabels.explicitIndustry ? industryLabels.label('location') : t('Location')}</th><th>{t('Required')}</th><th>{t('Filled')}</th><th>{t('Open')}</th><th>{t('Fill rate')}</th></tr></thead>
                    <tbody>{report.byLocation.map((row) => (
                      <tr key={row.locationId || row.locationName || 'location'}>
                        <td>{row.locationName || '-'}</td>
                        <td>{numberLabel(row.requiredHeadcount)}</td>
                        <td>{numberLabel(row.filledHeadcount)}</td>
                        <td>{numberLabel(row.openShifts)}</td>
                        <td>{numberLabel(row.fillRate, 1)}%</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              ) : <EmptyState title={t('No scheduled location rows')} description={t('No matching scheduled shifts were found.')} />}
            </section>
            <section className="stack" style={{ gap: '.75rem' }}>
              <h4 style={{ margin: 0 }}>{industryLabels.explicitIndustry ? `${industryLabels.label('job')} breakdown` : t('Job / order breakdown')}</h4>
              {report.byJob.length ? (
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead><tr><th>{industryLabels.explicitIndustry ? industryLabels.label('job') : t('Job / order')}</th><th>{t('Required')}</th><th>{t('Filled')}</th><th>{t('Open')}</th><th>{t('Completed')}</th><th>{t('Gross hours')}</th></tr></thead>
                    <tbody>{report.byJob.map((row) => (
                      <tr key={row.jobId || row.jobTitle || 'job'}>
                        <td>{row.jobTitle || '-'}</td>
                        <td>{numberLabel(row.requiredHeadcount)}</td>
                        <td>{numberLabel(row.filledHeadcount)}</td>
                        <td>{numberLabel(row.openShifts)}</td>
                        <td>{numberLabel(row.completedShifts)}</td>
                        <td>{numberLabel(row.grossHours, 2)}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              ) : <EmptyState title={t('No job rows')} description={t('No matching jobs or shifts were found.')} />}
            </section>
          </div>

          <DetailTable title={t('Attendance exceptions')} rows={report.attendanceExceptions} empty={t('No attendance exceptions')} labels={industryLabels.label} explicitIndustry={industryLabels.explicitIndustry} />
          <DetailTable title={t('Approval queue')} rows={report.approvalQueue} empty={t('No approval rows')} labels={industryLabels.label} explicitIndustry={industryLabels.explicitIndustry} />
        </>
      ) : null}
    </section>
  )
}
